import { Fragment, useState } from 'react';
import { Row } from '@tanstack/react-table';
import { SecondaryText } from '@monorepo/base/src/components/buttons/buttons';
import { SelectedLinerButton } from '@monorepo/base/src/components/table/action-liner/liner-buttons';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../stores';
import { bidderModelFilterPrototypes } from '../../components/filters-menu/filters';
import { ITableFiltersActions, TableFilters } from '@monorepo/base/src/components/table/table-filters/table-filters';
import { useRoute } from '../tools/use-route';
import { BidderApi } from '../../apis/bidder.api';
import { BidderModelTableModel } from '../../models/bidder-model.model';
import routes from '../../routes/index.routes';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';

export function useBidderModelsActions() {
	const { settingsStore, bidderStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const [isRebuilding, setIsRebuilding] = useState(false);
	const navigate = useNavigateParams();

	const filterStore = settingsStore
		.getPages()
		.get(currentRouteWithoutSpecialChars)
		?.tableStore.getFiltersStore(currentRouteWithoutSpecialChars);
	const currentFilters = filterStore?.getCurrentFilters();

	const DefaultAvailableFilters = [...bidderModelFilterPrototypes];

	const FiltersActions = (props: ITableFiltersActions) => {
		if (!currentFilters) {
			return null;
		}

		return (
			<TableFilters
				{...props}
				availableFilters={DefaultAvailableFilters}
				currentFilters={currentFilters}
				clearAll={() => filterStore?.clearAll()}
				clearFilter={index => filterStore?.clearFilter(index)}
				addFilter={filter => filterStore?.addFilter(filter)}
				editFilter={(index, filter) => filterStore?.editFilter(index, filter)}
			/>
		);
	};

	const SelectedActions = (props: { rows: Row<BidderModelTableModel>[] }) => {
		const { rows } = props;
		const canRebuild = rows.length === 1;
		const modelName = canRebuild ? rows[0].original.modelName : '';

		const onRebuild = async () => {
			if (!canRebuild || !modelName) {
				return;
			}
			setIsRebuilding(true);
			try {
				const result = await BidderApi.rebuildModel(modelName);
				if (result.success === true) {
					await bidderStore.modelsList.fetch();
				} else {
					window.alert('Something went wrong');
				}
			} catch (e) {
				console.error(e);
			} finally {
				setIsRebuilding(false);
			}
		};

		return (
			<Fragment>
				<SelectedLinerButton>
					<SecondaryText
						icon={'edit-05'}
						onClick={() => navigate(routes.bidder.editModel(encodeURIComponent(rows[0].original.modelName)))}
						textColor={'white'}
						disabled={rows.length !== 1}>
						Edit
					</SecondaryText>
				</SelectedLinerButton>
				<SelectedLinerButton>
					<SecondaryText
						icon={'refresh-ccw-01'}
						onClick={onRebuild}
						textColor={'white'}
						disabled={!canRebuild || isRebuilding}
						isRotateIcon={isRebuilding}>
						Rebuild
					</SecondaryText>
				</SelectedLinerButton>
			</Fragment>
		);
	};

	return {
		FiltersActions,
		SelectedActions,
	};
}
